import React, { useEffect, useState, useCallback } from "react";
import {
  Row,
  Col,
  Button,
  Progress,
  List,
  Typography,
  Tag,
  Spin,
  message,
  Tooltip,
  Modal,
} from "antd";
import {
  ArrowUpOutlined,
  ArrowDownOutlined,
  WarningOutlined,
  CloseCircleOutlined,
  ClockCircleOutlined,
  UserOutlined,
  LineChartOutlined,
  SettingOutlined,
  CheckCircleOutlined,
  InfoCircleOutlined,
} from "@ant-design/icons";
import {
  KPICard,
  ChartCard,
  FilterButton,
  DurationBadge,
} from "../../components/applicationsOverview";
import { MiniMetric } from "../../components/applicationsOverview/MiniMetric";
import { CardWithHeader } from "../../components/applicationsOverview/CardWithHeader";
import { FilterableDashboard } from "./filterable-dashboard";
import "../../styles/applicationsOverview.scss";
import { colors, spacing, fontSize } from "../../styles/theme";
import {
  OverviewData,
  AlertItem,
  OverviewAlerts,
  fetchOverviewMetrics,
  fetchOverviewTrends,
  fetchOverviewAlerts,
} from "../../services/applicationsOverviewService";
import ApplicationsOverviewSettingsModal from "../../components/ApplicationsOverviewSettingsModal";
import { useSelector } from "react-redux";
import { selectMyUser } from "../../models/users/myUserSlice";
import {
  formatDuration,
  formatTimestamp as formatTimestampUtil,
} from "../../utils/formatDuration";
import { getAgeInMinutes } from "../../utils/dateUtils";
import { getMockOverviewData } from "../../utils/mockApplicationsOverviewData";
import { StatusBadge } from "../../components/applicationsOverview/components/StatusBadge";
import { ApplicationsByStatusChart } from "../../components/applicationsOverview/charts/ApplicationsByStatusChart";
import {
  ApplicationsByFlowChart,
  FlowChartLegend,
} from "../../components/applicationsOverview/charts/ApplicationsByFlowChart";
import { WorkloadByAdminChart } from "../../components/applicationsOverview/charts/WorkloadByAdminChart";
import {
  VolumeTrendChart,
  VolumeTrendLegend,
} from "../../components/applicationsOverview/charts/VolumeTrendChart";
import { EfficiencyChart } from "../../components/applicationsOverview/charts/EfficiencyChart";
import { ApplicationAgingChart } from "../../components/applicationsOverview/charts/ApplicationAgingChart";
import { getAgeBasedColor } from "../../components/applicationsOverview/utils/overviewColors";
import type { DrilldownFilter } from "../../components/applicationsOverview/types";

const { Title, Text, Paragraph } = Typography;

// Constants for applications-overview configuration
const AUTO_REFRESH_INTERVAL_MS = 30000; // 30 seconds
const MAX_CONSECUTIVE_ERRORS = 3;

// Sanitize text to prevent XSS - removes HTML tags and limits length
const sanitizeText = (text: string, maxLength: number = 200): string => {
  if (!text) return "";
  // Remove HTML tags
  const withoutTags = text.replace(/<[^>]*>/g, "");
  // Limit length
  return withoutTags.slice(0, maxLength);
};

interface ApplicationsOverviewProps {
  fiSlug?: string;
  userId?: number;
}

export const ApplicationsOverview = ({
  fiSlug,
  userId,
}: ApplicationsOverviewProps) => {
  // Check URL parameter for mock data mode (e.g., ?mock=true)
  const [useMockData] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    return params.get("mock") === "true";
  });

  const [overviewData, setOverviewData] = useState<OverviewData | null>(
    null
  );
  const [alertsData, setAlertsData] = useState<OverviewAlerts | null>(null);

  // Derived from alertsData to avoid redundant state
  const alerts = alertsData?.alerts || [];
  const [loading, setLoading] = useState(true);
  const [lastUpdated, setLastUpdated] = useState<Date>(new Date());
  const [metricsCacheUntil, setMetricsCacheUntil] = useState<Date | null>(null);
  const [trendsCacheUntil, setTrendsCacheUntil] = useState<Date | null>(null);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [selectedAlertFilter, setSelectedAlertFilter] = useState<
    "all" | "critical" | "warning" | "info"
  >("all");
  const [settingsModalVisible, setSettingsModalVisible] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [consecutiveErrors, setConsecutiveErrors] = useState(0);
  const isFetchingRef = React.useRef(false);

  // Drilldown modal state
  const [modalVisible, setModalVisible] = useState(false);
  const [modalTitle, setModalTitle] = useState("Applications");
  const [modalFilter, setModalFilter] = useState<{
    assignedToMe?: boolean;
    decisionStatus?: string;
  }>({});
  const [modalOverviewFilter, setModalOverviewFilter] = useState<
    string | undefined
  >(undefined);
  const [modalSearch, setModalSearch] = useState("");

  // Handlers for the embedded FilterableDashboard list view
  const [clearColumnFiltersHandler, setClearColumnFiltersHandler] = useState<
    (() => void) | null
  >(null);
  const [exportCsvHandler, setExportCsvHandler] = useState<(() => void) | null>(
    null
  );

  // Get user to check for portal admin role
  const myUser = useSelector(selectMyUser);
  const isPortalAdmin = myUser?.roles?.portalAdmin || false;

  // Helper functions for computed values
  const getEfficiencyMetrics = useCallback((data: OverviewData | null) => {
    const decisionTime = data?.aggregateByDecisionTime;
    if (!decisionTime) {
      return {
        percentage: 0,
        percentageChange: 0,
        distribution: {
          lessThan1d: 0,
          oneTo2d: 0,
          twoTo4d: 0,
          fourTo7d: 0,
          moreThan7d: 0,
        },
      };
    }

    const total = Object.values(decisionTime.distribution).reduce(
      (a, b) => a + b,
      0
    );
    const previousTotal = Object.values(
      decisionTime.previousDistribution
    ).reduce((a, b) => a + b, 0);

    const percentage = total
      ? Math.round((decisionTime.distribution.lessThan1d / total) * 100)
      : 0;
    const previousPercentage = previousTotal
      ? Math.round(
          (decisionTime.previousDistribution.lessThan1d / previousTotal) * 100
        )
      : 0;

    return {
      percentage,
      percentageChange: percentage - previousPercentage,
      distribution: decisionTime.distribution,
    };
  }, []);

  const getTimeToDecisionChange = useCallback((data: OverviewData | null) => {
    const decisionTime = data?.aggregateByDecisionTime;
    if (!decisionTime) return 0;
    return decisionTime.average - decisionTime.previousAverage;
  }, []);

  const getGreeting = useCallback(() => {
    const hour = new Date().getHours();
    if (hour < 12) return "Good morning";
    if (hour < 18) return "Good afternoon";
    return "Good evening";
  }, []);

  const getUserDisplayName = useCallback(() => {
    if (myUser?.firstName) {
      return myUser.firstName;
    }
    return "Operations";
  }, [myUser]);

  // Handler to open modal with specific filter
  const openDrawer = useCallback(
    (
      filter: { assignedToMe?: boolean; decisionStatus?: string } | string,
      title: string = "Applications",
      assignedToMe?: boolean
    ) => {
      if (typeof filter === "string") {
        // Overview filter (custom filter string)
        setModalOverviewFilter(filter);
        setModalFilter(assignedToMe ? { assignedToMe: true } : {});
      } else {
        // Legacy filter object
        setModalFilter(filter);
        setModalOverviewFilter(undefined);
      }
      // Sanitize title to prevent XSS from backend data
      setModalTitle(sanitizeText(title));
      setModalVisible(true);
    },
    []
  );

  // Handler to close modal
  const closeDrawer = useCallback(() => {
    setModalVisible(false);
    setModalSearch(""); // Clear search when closing modal
  }, []);

  // Handler to clear all filters in modal
  const clearAllFilters = useCallback(() => {
    setModalSearch("");
    if (clearColumnFiltersHandler) {
      clearColumnFiltersHandler();
    }
  }, [clearColumnFiltersHandler]);

  // Handler to export CSV
  const handleExportCsv = useCallback(() => {
    if (exportCsvHandler) {
      exportCsvHandler();
    }
  }, [exportCsvHandler]);

  // Fetch overview data from real APIs (split into metrics and trends)
  const fetchRealData = useCallback(
    async (forceRefreshTrends = false, forceRefreshMetrics = false) => {
      // Prevent concurrent requests
      if (isFetchingRef.current) {
        console.log("Fetch already in progress, skipping...");
        return;
      }

      if (!fiSlug) {
        console.error("Organization slug is missing");
        setError("Organization slug is missing");
        setLoading(false);
        return;
      }

      try {
        isFetchingRef.current = true;
        setLoading(true);
        setError(null);

        // Use mock data if enabled via URL parameter (?mock=true)
        if (useMockData) {
          const mockData = getMockOverviewData();
          // Transform mock alerts to match AlertItem interface
          // Split affectedCount between assigned (60-70%) and unassigned (30-40%)
          const mockAlerts = mockData.alerts.map((alert) => {
            const totalCount = alert.affectedCount || 0;
            const assignedRatio = 0.6 + Math.random() * 0.1; // 60-70%
            const assignedCount = Math.floor(totalCount * assignedRatio);
            const unassignedCount = totalCount - assignedCount;

            return {
              ...alert,
              assignedToMeCount: assignedCount,
              unassignedCount: unassignedCount,
            };
          });

          // Calculate unassigned count for critical metrics
          const totalUnassigned = mockAlerts.reduce(
            (sum, alert) => sum + (alert.unassignedCount || 0),
            0
          );

          setOverviewData(mockData as any);
          setAlertsData({
            alerts: mockAlerts,
            criticalMetrics: {
              unassigned: mockData.unassignedCount || 0,
              unassignedThreshold: 10,
              failedBookings: 0,
            },
            metadata: {
              timestamp: mockData.metadata.timestamp,
              nextRefreshAt: mockData.metadata.cacheUntil,
            },
          });
          setLastUpdated(new Date());
          setLoading(false);
          isFetchingRef.current = false;
          return;
        }

        const now = new Date();

        // Determine if we should fetch metrics based on cache expiry
        const shouldFetchMetrics =
          forceRefreshMetrics || !metricsCacheUntil || now > metricsCacheUntil;

        // Determine if we should fetch trends based on cache expiry
        const shouldFetchTrends =
          forceRefreshTrends || !trendsCacheUntil || now > trendsCacheUntil;

        // Build requests array
        const requests: Promise<any>[] = [fetchOverviewAlerts(fiSlug)];
        let metricsIndex = -1;
        let trendsIndex = -1;

        if (shouldFetchMetrics) {
          metricsIndex = requests.length;
          requests.push(fetchOverviewMetrics(fiSlug));
        }

        if (shouldFetchTrends) {
          trendsIndex = requests.length;
          requests.push(fetchOverviewTrends(fiSlug));
        }

        const results = await Promise.all(requests);
        const fetchedAlertsData = results[0];
        const metricsData = metricsIndex >= 0 ? results[metricsIndex] : null;
        const trendsData = trendsIndex >= 0 ? results[trendsIndex] : null;

        // Set overview data and alerts using functional update to avoid stale closure
        setOverviewData((prevData) => {
          // Build combined data explicitly to avoid preserving stale fields
          const combinedData: OverviewData = {
            // Base/card data - from metrics endpoint
            newApplications: metricsData?.newApplications ||
              prevData?.newApplications || {
                period: "24h",
                count: 0,
                previousPeriodCount: 0,
              },
            pendingApplications: metricsData?.pendingApplications ||
              prevData?.pendingApplications || { count: 0 },
            unreadApplications: metricsData?.unreadApplications ||
              prevData?.unreadApplications || { count: 0 },
            newActivity: metricsData?.newActivity ||
              prevData?.newActivity || {
                period: "24h",
                count: 0,
                previousPeriodCount: 0,
              },
            recentBookings: metricsData?.recentBookings ||
              prevData?.recentBookings || {
                period: "24h",
                count: 0,
                previousPeriodCount: 0,
              },
            coreFailures: metricsData?.coreFailures || prevData?.coreFailures,

            // Aggregate data - from metrics endpoint
            aggregateByDecisionStatus:
              metricsData?.aggregateByDecisionStatus ||
              prevData?.aggregateByDecisionStatus,
            aggregateByAdmin:
              metricsData?.aggregateByAdmin || prevData?.aggregateByAdmin,
            aggregateByLastActivity:
              metricsData?.aggregateByLastActivity ||
              prevData?.aggregateByLastActivity,
            aggregateBySubmissionDate:
              metricsData?.aggregateBySubmissionDate ||
              prevData?.aggregateBySubmissionDate,
            unassignedCount:
              metricsData?.unassignedCount ?? prevData?.unassignedCount,

            // Trend data - from trends endpoint
            aggregateByFlow:
              trendsData?.aggregateByFlow || prevData?.aggregateByFlow,
            aggregateByDecisionTime:
              trendsData?.aggregateByDecisionTime ||
              prevData?.aggregateByDecisionTime,
            decisionMetrics:
              trendsData?.decisionMetrics || prevData?.decisionMetrics,

            // Metadata - use most recent
            metadata: metricsData?.metadata ||
              trendsData?.metadata ||
              prevData?.metadata || {
                timestamp: new Date().toISOString(),
              },
          };
          return combinedData;
        });
        setAlertsData(fetchedAlertsData);
        setLastUpdated(new Date());

        // Update cache expiry times from backend
        if (metricsData?.metadata?.cacheUntil) {
          setMetricsCacheUntil(new Date(metricsData.metadata.cacheUntil));
        }
        if (trendsData?.metadata?.cacheUntil) {
          setTrendsCacheUntil(new Date(trendsData.metadata.cacheUntil));
        }

        setConsecutiveErrors(0); // Reset error count on success
      } catch (error) {
        console.error("Failed to fetch overview data:", error);
        const errorMessage = "Failed to load overview data. Please try again.";
        setError(errorMessage);

        // Increment consecutive errors
        setConsecutiveErrors((prev) => {
          const newCount = prev + 1;

          // If we've had MAX_CONSECUTIVE_ERRORS, disable auto-refresh
          if (newCount >= MAX_CONSECUTIVE_ERRORS) {
            setAutoRefresh(false);
            message.error(
              "Multiple errors occurred. Auto-refresh has been disabled. Please refresh manually or check your connection.",
              5
            );
          } else {
            message.error(errorMessage);
          }

          return newCount;
        });
      } finally {
        setLoading(false);
        isFetchingRef.current = false;
      }
    },
    [fiSlug, metricsCacheUntil, trendsCacheUntil]
  );

  // Initial load - fetch when component mounts or fiSlug changes
  useEffect(() => {
    if (fiSlug) {
      fetchRealData(true, true); // Force fetch both on initial load
    }
    // Dependencies: fiSlug triggers reload when organization changes
    // fetchRealData is stable due to useCallback with proper dependencies
  }, [fiSlug, fetchRealData]);

  // Auto-refresh based on cache expiry
  useEffect(() => {
    if (!autoRefresh) return;

    const interval = setInterval(() => {
      fetchRealData(false, false); // Respect cache expiry times
    }, AUTO_REFRESH_INTERVAL_MS); // Check every 30 seconds, but only fetch if cache expired

    return () => clearInterval(interval);
  }, [autoRefresh, fetchRealData]);

  // Drilldown handler - opens drawer with specific filter
  const handleDrilldown = (filter: DrilldownFilter, title?: string) => {
    // Use customFilter if available, otherwise pass the filter object
    if (filter.customFilter) {
      openDrawer(filter.customFilter, title, filter.assignedToMe);
    } else {
      openDrawer(filter, title);
    }
  };

  const formatTimestamp = (date: Date): string => {
    return date.toLocaleTimeString("en-US", {
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  // Show error state if there's an error and no data
  if (error && !overviewData) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
          flexDirection: "column",
        }}
      >
        <CloseCircleOutlined
          style={{ fontSize: 48, color: colors.brandCoral, marginBottom: 16 }}
        />
        <Title level={3}>Failed to Load Overview</Title>
        <Paragraph type="secondary">{error}</Paragraph>
        <Button
          type="primary"
          onClick={() => {
            setError(null);
            setConsecutiveErrors(0);
            setAutoRefresh(true);
            fetchRealData(true, true);
          }}
        >
          Retry
        </Button>
      </div>
    );
  }

  if (loading && !overviewData) {
    return (
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          minHeight: "60vh",
          gap: 16,
        }}
      >
        <Spin size="large" />
        <Text style={{ marginTop: spacing[4] }}>Loading overview...</Text>
      </div>
    );
  }

  if (!overviewData) return null;

  // Compute derived values from overview data
  const efficiencyMetrics = getEfficiencyMetrics(overviewData);
  const timeToDecisionChange = getTimeToDecisionChange(overviewData);

  const filteredAlerts =
    selectedAlertFilter === "all"
      ? alerts
      : alerts.filter((a) => a.severity === selectedAlertFilter);

  const criticalCount = alerts.filter((a) => a.severity === "critical").length;
  const warningCount = alerts.filter((a) => a.severity === "warning").length;
  const infoCount = alerts.filter((a) => a.severity === "info").length;

  return (
    <div
      className="applications-overview-container"
      style={{
        marginTop: 0,
        marginBottom: spacing[4],
        paddingLeft: spacing[4],
        paddingRight: spacing[4],
      }}
    >
      {/* Left Sidebar - Alerts */}
      <aside className="ao-sidebar">
        <div className="sidebar-header">
          <Title level={4}>
            {getGreeting()}, {getUserDisplayName()}!
          </Title>
          <div
            className="secondary"
            style={{ display: "flex", alignItems: "center", gap: spacing[1] }}
          >
            <Text type="secondary" style={{ fontSize: fontSize[3] }}>
              {formatTimestamp(lastUpdated)} •{" "}
              <Button
                type="link"
                size="small"
                onClick={() => setAutoRefresh(!autoRefresh)}
                style={{
                  padding: 0,
                  height: "auto",
                  fontSize: fontSize[3],
                  color: colors.textSecondary,
                  textDecoration: "underline",
                  boxShadow: "none",
                  outline: "none",
                }}
              >
                {autoRefresh ? "Auto-refresh on" : "Auto-refresh off"}
              </Button>
              {useMockData && (
                <>
                  {" • "}
                  <Tag color="orange" style={{ margin: 0 }}>
                    MOCK DATA
                  </Tag>
                </>
              )}
            </Text>
            {isPortalAdmin && (
              <Button
                type="text"
                icon={<SettingOutlined />}
                onClick={() => setSettingsModalVisible(true)}
                title="Overview Settings"
                style={{
                  padding: "4px 8px",
                  height: "auto",
                  border: "none",
                  boxShadow: "none",
                }}
                className="settings-button-no-focus"
              />
            )}
          </div>
        </div>

        <div className="sidebar-filters">
          <FilterButton
            label="All"
            count={alerts.length}
            isActive={selectedAlertFilter === "all"}
            onClick={() => setSelectedAlertFilter("all")}
          />
          <FilterButton
            label="Critical"
            count={criticalCount}
            isActive={selectedAlertFilter === "critical"}
            onClick={() => setSelectedAlertFilter("critical")}
            variant="critical"
          />
          <FilterButton
            label="Warning"
            count={warningCount}
            isActive={selectedAlertFilter === "warning"}
            onClick={() => setSelectedAlertFilter("warning")}
            variant="warning"
          />
        </div>

        <div className="sidebar-alerts">
          {filteredAlerts.length === 0 ? (
            <div
              style={{
                padding: spacing[6],
                textAlign: "center",
                background: colors.white,
                borderRadius: 10,
                border: `2px dashed ${colors.gray}`,
                margin: "0 auto",
              }}
            >
              <Text type="secondary" style={{ fontSize: fontSize[2] }}>
                No new notifications
              </Text>
            </div>
          ) : (
            <List
              dataSource={filteredAlerts}
              renderItem={(alert) => (
                <List.Item>
                  <KPICard
                    title={alert.title}
                    value={alert.assignedToMeCount}
                    duration={alert.duration}
                    suffix={
                      <StatusBadge severity={alert.severity}>
                        {alert.severity === "critical"
                          ? "CRITICAL"
                          : alert.severity === "warning"
                          ? "WARNING"
                          : "INFO"}
                      </StatusBadge>
                    }
                    severity={alert.severity}
                    onClick={
                      alert.assignedToMeCount > 0
                        ? () =>
                            handleDrilldown(
                              alert.drilldownFilter || {},
                              alert.drilldownTitle
                            )
                        : undefined
                    }
                    hoverable={alert.assignedToMeCount > 0}
                  >
                    {/* Subtitle: "+ X unassigned" */}
                    {alert.unassignedCount > 0 && (
                      <Text
                        type="secondary"
                        style={{
                          fontSize: 13,
                          display: "block",
                          marginBottom: 8,
                          cursor: "pointer",
                        }}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDrilldown(
                            alert.unassignedDrilldownFilter || {},
                            alert.unassignedDrilldownTitle
                          );
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.textDecoration = "underline";
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.textDecoration = "none";
                        }}
                      >
                        + {alert.unassignedCount} unassigned
                      </Text>
                    )}

                    {/* Breakdown for stalled applications */}
                    {alert.breakdown && (
                      <div
                        style={{
                          display: "flex",
                          gap: 8,
                          marginBottom: 8,
                          flexWrap: "wrap",
                        }}
                      >
                        {alert.breakdown.infoDays.count > 0 && (
                          <Tag color="blue">
                            {alert.breakdown.infoDays.label}:{" "}
                            {alert.breakdown.infoDays.count}
                          </Tag>
                        )}
                        {alert.breakdown.warningDays.count > 0 && (
                          <Tag color="orange">
                            {alert.breakdown.warningDays.label}:{" "}
                            {alert.breakdown.warningDays.count}
                          </Tag>
                        )}
                        {alert.breakdown.criticalDays.count > 0 && (
                          <Tag color="red">
                            {alert.breakdown.criticalDays.label}:{" "}
                            {alert.breakdown.criticalDays.count}
                          </Tag>
                        )}
                      </div>
                    )}

                    {/* Most recent timestamp */}
                    {alert.mostRecentTimestamp && (
                      <Text
                        type="secondary"
                        style={{
                          fontSize: 12,
                          display: "block",
                          color:
                            alert.id === "core-failures" &&
                            alert.severity === "critical"
                              ? "#ff4d4f"
                              : undefined,
                        }}
                      >
                        Most recent:{" "}
                        {formatTimestampUtil(alert.mostRecentTimestamp)}
                      </Text>
                    )}
                  </KPICard>
                </List.Item>
              )}
            />
          )}
        </div>
      </aside>

      {/* Main Content Area */}
      <main className="ao-main">
        {/* Key Metrics */}
        <Row gutter={[16, 16]}>
          <Col xs={24} sm={12} lg={6}>
            <KPICard
              title="New Applications"
              duration="24h"
              value={overviewData.newApplications.count}
              valueStyle={{ color: colors.gray9 }}
              suffix={
                <div className="metric-change-inline">
                  {(() => {
                    const change =
                      overviewData.newApplications.count -
                      overviewData.newApplications.previousPeriodCount;
                    return change >= 0 ? (
                      <Tag color="success" icon={<ArrowUpOutlined />}>
                        {Math.abs(change)}
                      </Tag>
                    ) : (
                      <Tag color="error" icon={<ArrowDownOutlined />}>
                        {Math.abs(change)}
                      </Tag>
                    );
                  })()}
                </div>
              }
              hoverable
              onClick={() =>
                openDrawer("new_24h", "New Applications (Last 24 hours)")
              }
            >
              {overviewData.newApplications.maxCreatedAt ? (
                <Text type="secondary" style={{ fontSize: 12 }}>
                  Latest received{" "}
                  {(() => {
                    const age = getAgeInMinutes(
                      overviewData.newApplications.maxCreatedAt
                    );
                    return age !== undefined
                      ? `${formatDuration(age)} ago`
                      : "recently";
                  })()}
                </Text>
              ) : (
                <Text
                  type="secondary"
                  style={{ fontSize: 12, marginTop: 4, display: "block" }}
                >
                  Last 24 hours
                </Text>
              )}
            </KPICard>
          </Col>

          <Col xs={24} sm={12} lg={6}>
            <KPICard
              title="Pending Applications"
              value={overviewData.pendingApplications.count}
              valueStyle={{
                color: (() => {
                  const ageMinutes = getAgeInMinutes(
                    overviewData.pendingApplications.minCreatedAt
                  );
                  return ageMinutes !== undefined
                    ? getAgeBasedColor(ageMinutes, "value")
                    : colors.gray9;
                })(),
              }}
              prefix={
                overviewData.pendingApplications.minCreatedAt &&
                (() => {
                  const ageMinutes = getAgeInMinutes(
                    overviewData.pendingApplications.minCreatedAt
                  );
                  if (ageMinutes === undefined) return null;
                  const ageDays = ageMinutes / 60 / 24;
                  // Show warning icon if oldest is 2+ days
                  if (ageDays >= 2) {
                    return (
                      <WarningOutlined
                        style={{
                          color: getAgeBasedColor(ageMinutes, "value"),
                        }}
                      />
                    );
                  }
                  return null;
                })()
              }
              hoverable
              onClick={() => openDrawer("pending", "Pending Applications")}
            >
              {overviewData.pendingApplications.minCreatedAt &&
                (() => {
                  const ageMinutes = getAgeInMinutes(
                    overviewData.pendingApplications.minCreatedAt
                  );
                  if (ageMinutes === undefined) return null;
                  return (
                    <Text
                      style={{
                        fontSize: 12,
                        color: getAgeBasedColor(ageMinutes, "text"),
                      }}
                    >
                      Oldest received {formatDuration(ageMinutes)} ago
                    </Text>
                  );
                })()}
            </KPICard>
          </Col>

          <Col xs={24} sm={12} lg={6}>
            <KPICard
              title="Unread Applications"
              value={overviewData.unreadApplications.count}
              valueStyle={{
                color: (() => {
                  const ageMinutes = getAgeInMinutes(
                    overviewData.unreadApplications.minCreatedAt
                  );
                  if (ageMinutes === undefined) return colors.gray9;
                  const ageHours = ageMinutes / 60;
                  // Color coding: warn if > 24 hours
                  if (ageHours >= 24) return colors.brandOrange;
                  return colors.gray9; // < 24h (normal black)
                })(),
              }}
              prefix={
                overviewData.unreadApplications.minCreatedAt &&
                (() => {
                  const ageMinutes = getAgeInMinutes(
                    overviewData.unreadApplications.minCreatedAt
                  );
                  if (ageMinutes === undefined) return null;
                  const ageHours = ageMinutes / 60;
                  // Show warning icon if > 24 hours
                  if (ageHours >= 24) {
                    return (
                      <WarningOutlined style={{ color: colors.brandOrange }} />
                    );
                  }
                  return null;
                })()
              }
              hoverable
              onClick={() => openDrawer("unread", "Unread Applications")}
            >
              {overviewData.unreadApplications.maxCreatedAt !== undefined &&
                overviewData.unreadApplications.minCreatedAt !== undefined &&
                (() => {
                  const earliestAgeMinutes = getAgeInMinutes(
                    overviewData.unreadApplications.minCreatedAt
                  );
                  const latestAgeMinutes = getAgeInMinutes(
                    overviewData.unreadApplications.maxCreatedAt
                  );
                  if (
                    earliestAgeMinutes === undefined ||
                    latestAgeMinutes === undefined
                  )
                    return null;

                  const earliestAgeHours = earliestAgeMinutes / 60;

                  return (
                    <Text
                      style={{
                        fontSize: 12,
                        color:
                          earliestAgeHours >= 24
                            ? colors.brandOrange
                            : colors.gray7,
                      }}
                    >
                      {earliestAgeHours >= 24
                        ? `Oldest received ${formatDuration(
                            earliestAgeMinutes
                          )} ago`
                        : `Latest received ${formatDuration(
                            latestAgeMinutes
                          )} ago`}
                    </Text>
                  );
                })()}
            </KPICard>
          </Col>

          <Col xs={24} sm={12} lg={6}>
            <KPICard
              title="Applications with New Activity"
              value={overviewData.newActivity.count}
              valueStyle={{
                color: (() => {
                  const ageMinutes = getAgeInMinutes(
                    overviewData.newActivity.minCreatedAt
                  );
                  return ageMinutes !== undefined
                    ? getAgeBasedColor(ageMinutes, "value")
                    : colors.gray9;
                })(),
              }}
              prefix={
                overviewData.newActivity.minCreatedAt &&
                (() => {
                  const ageMinutes = getAgeInMinutes(
                    overviewData.newActivity.minCreatedAt
                  );
                  if (ageMinutes === undefined) return null;
                  const ageDays = ageMinutes / 60 / 24;
                  // Show warning icon if earliest activity is 2+ days old
                  if (ageDays >= 2) {
                    return (
                      <WarningOutlined
                        style={{
                          color: getAgeBasedColor(ageMinutes, "value"),
                        }}
                      />
                    );
                  }
                  return null;
                })()
              }
              hoverable
              onClick={() => openDrawer("new_activity", "New Activity")}
            >
              {overviewData.newActivity.maxCreatedAt !== undefined &&
                overviewData.newActivity.minCreatedAt !== undefined &&
                (() => {
                  const earliestAgeMinutes = getAgeInMinutes(
                    overviewData.newActivity.minCreatedAt
                  );
                  const latestAgeMinutes = getAgeInMinutes(
                    overviewData.newActivity.maxCreatedAt
                  );
                  if (
                    earliestAgeMinutes === undefined ||
                    latestAgeMinutes === undefined
                  )
                    return null;

                  const earliestAgeDays = earliestAgeMinutes / 60 / 24;

                  return (
                    <Text
                      style={{
                        fontSize: 12,
                        color: getAgeBasedColor(earliestAgeMinutes, "text"),
                      }}
                    >
                      {earliestAgeDays >= 2
                        ? `Unread activity from ${formatDuration(
                            earliestAgeMinutes
                          )} ago`
                        : `Latest activity ${formatDuration(
                            latestAgeMinutes
                          )} ago`}
                    </Text>
                  );
                })()}
            </KPICard>
          </Col>
        </Row>

        {/* Secondary Metrics */}
        <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
          <Col xs={24} sm={12} md={6}>
            <KPICard
              title={
                <>
                  Recent Bookings{" "}
                  <Tooltip title="Core/LOS bookings in the last 24 hours">
                    <InfoCircleOutlined
                      style={{ fontSize: 14, color: "#8c8c8c" }}
                    />
                  </Tooltip>
                </>
              }
              duration="24h"
              value={overviewData.recentBookings.count}
              valueStyle={{
                color:
                  overviewData.recentBookings.count > 0
                    ? "#52c41a"
                    : "#8c8c8c",
              }}
              suffix={
                <div className="metric-change-inline">
                  {(() => {
                    const change =
                      overviewData.recentBookings.count -
                      overviewData.recentBookings.previousPeriodCount;
                    return change >= 0 ? (
                      <Tag color="success" icon={<ArrowUpOutlined />}>
                        {Math.abs(change)}
                      </Tag>
                    ) : (
                      <Tag color="error" icon={<ArrowDownOutlined />}>
                        {Math.abs(change)}
                      </Tag>
                    );
                  })()}
                </div>
              }
              prefix={
                overviewData.recentBookings.count > 0 ? (
                  <CheckCircleOutlined />
                ) : (
                  <ClockCircleOutlined />
                )
              }
              hoverable
              onClick={() =>
                openDrawer("complete_24h", "Core Bookings (Last 24 hours)")
              }
            >
              {overviewData.recentBookings.maxCreatedAt && (
                <Text type="secondary" style={{ fontSize: 12 }}>
                  Most recent:{" "}
                  {formatTimestampUtil(
                    overviewData.recentBookings.maxCreatedAt
                  )}
                </Text>
              )}
            </KPICard>
          </Col>

          <Col xs={24} sm={12} md={6}>
            <KPICard
              title="Same-Day Decisions"
              duration="30d"
              value={`${efficiencyMetrics.percentage.toString()}%`}
              suffix={
                <>
                  {efficiencyMetrics.percentageChange !== 0 && (
                    <span style={{ marginLeft: 8 }}>
                      {efficiencyMetrics.percentageChange > 0 ? (
                        <Tag
                          color="success"
                          icon={<ArrowUpOutlined />}
                          style={{ marginLeft: 0 }}
                        >
                          {Math.abs(efficiencyMetrics.percentageChange)}%
                        </Tag>
                      ) : (
                        <Tag
                          color="error"
                          icon={<ArrowDownOutlined />}
                          style={{ marginLeft: 0 }}
                        >
                          {Math.abs(efficiencyMetrics.percentageChange)}%
                        </Tag>
                      )}
                    </span>
                  )}
                </>
              }
              valueStyle={{ color: "#262626" }}
              hoverable
              onClick={() =>
                openDrawer(
                  "decisioned_lessThan1d_30d",
                  "Same-Day Decisions (Last Month)"
                )
              }
            >
              <Progress
                percent={efficiencyMetrics.percentage}
                showInfo={false}
                strokeColor={colors.brandPrimary}
                style={{ marginTop: 8 }}
              />
            </KPICard>
          </Col>

          <Col xs={24} sm={12} md={6}>
            <KPICard
              title={
                <span>
                  Auto-Decisions{" "}
                  <Tooltip title="Applications that received a rule-based status assignment without manual intervention">
                    <InfoCircleOutlined
                      style={{ fontSize: 12, color: "#8c8c8c" }}
                    />
                  </Tooltip>
                </span>
              }
              duration="30d"
              value={`${(() => {
                const metrics = overviewData.decisionMetrics;
                if (!metrics) return "0.0";
                const rate =
                  metrics.count > 0
                    ? (metrics.autoDecisions / metrics.count) * 100
                    : 0;
                return rate.toFixed(1);
              })()}%`}
              suffix={
                <>
                  {(() => {
                    const metrics = overviewData.decisionMetrics;
                    if (!metrics) return null;

                    const currentRate =
                      metrics.count > 0
                        ? (metrics.autoDecisions / metrics.count) * 100
                        : 0;
                    const previousRate =
                      metrics.previousPeriodCount > 0
                        ? (metrics.previousPeriodAutoDecisions /
                            metrics.previousPeriodCount) *
                          100
                        : 0;
                    const change = currentRate - previousRate;

                    if (change === 0) return null;

                    return (
                      <span style={{ marginLeft: 8 }}>
                        {change > 0 ? (
                          <Tag
                            color="success"
                            icon={<ArrowUpOutlined />}
                            style={{ marginLeft: 0 }}
                          >
                            {Math.abs(change).toFixed(1)}%
                          </Tag>
                        ) : (
                          <Tag
                            color="error"
                            icon={<ArrowDownOutlined />}
                            style={{ marginLeft: 0 }}
                          >
                            {Math.abs(change).toFixed(1)}%
                          </Tag>
                        )}
                      </span>
                    );
                  })()}
                </>
              }
              valueStyle={{ color: "#262626" }}
              hoverable
              onClick={() =>
                openDrawer("auto_decisioned_30d", "Auto-Decisions (Last Month)")
              }
            >
              <Progress
                percent={parseFloat(
                  (() => {
                    const metrics = overviewData.decisionMetrics;
                    if (!metrics) return "0";
                    const rate =
                      metrics.count > 0
                        ? (metrics.autoDecisions / metrics.count) * 100
                        : 0;
                    return rate.toFixed(1);
                  })()
                )}
                showInfo={false}
                strokeColor="#52c1b2"
                style={{ marginTop: 8 }}
              />
            </KPICard>
          </Col>

          <Col xs={24} sm={12} md={6}>
            <CardWithHeader
              title={
                <span>
                  Outcomes{" "}
                  <Tooltip title="Breakdown of decision outcomes">
                    <InfoCircleOutlined
                      style={{ fontSize: 12, color: "#8c8c8c" }}
                    />
                  </Tooltip>
                </span>
              }
              duration="30d"
            >
              {(() => {
                const metrics = overviewData.decisionMetrics;
                if (!metrics) return null;

                const total =
                  metrics.approved + metrics.denied + metrics.canceled;
                const approvedPct =
                  total > 0 ? (metrics.approved / total) * 100 : 0;
                const deniedPct =
                  total > 0 ? (metrics.denied / total) * 100 : 0;
                const canceledPct =
                  total > 0 ? (metrics.canceled / total) * 100 : 0;

                const prevTotal =
                  metrics.previousPeriodApproved +
                  metrics.previousPeriodDenied +
                  metrics.previousPeriodCanceled;
                const prevApprovedPct =
                  prevTotal > 0
                    ? (metrics.previousPeriodApproved / prevTotal) * 100
                    : 0;
                const prevDeniedPct =
                  prevTotal > 0
                    ? (metrics.previousPeriodDenied / prevTotal) * 100
                    : 0;
                const prevCanceledPct =
                  prevTotal > 0
                    ? (metrics.previousPeriodCanceled / prevTotal) * 100
                    : 0;

                return (
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-around",
                      alignItems: "center",
                      paddingTop: 2,
                    }}
                  >
                    <MiniMetric
                      label={`${metrics.approved.toLocaleString()} Approved`}
                      percentage={approvedPct}
                      count={metrics.approved}
                      change={approvedPct - prevApprovedPct}
                      color="#52c41a"
                      onClick={() =>
                        openDrawer(
                          "approved_30d",
                          "Approved Applications (Last Month)"
                        )
                      }
                    />
                    <MiniMetric
                      label={`${metrics.denied.toLocaleString()} Denied`}
                      percentage={deniedPct}
                      count={metrics.denied}
                      change={deniedPct - prevDeniedPct}
                      color="#ff4d4f"
                      onClick={() =>
                        openDrawer(
                          "denied_30d",
                          "Denied Applications (Last Month)"
                        )
                      }
                    />
                    <MiniMetric
                      label={`${metrics.canceled.toLocaleString()} Canceled`}
                      percentage={canceledPct}
                      count={metrics.canceled}
                      change={canceledPct - prevCanceledPct}
                      color="#8c8c8c"
                      onClick={() =>
                        openDrawer(
                          "canceled_30d",
                          "Canceled Applications (Last Month)"
                        )
                      }
                    />
                  </div>
                );
              })()}
            </CardWithHeader>
          </Col>
        </Row>

        {/* Charts Section */}
        <Row gutter={[16, 16]} style={{ marginTop: 24 }}>
          {/* Application Aging */}
          <Col xs={24} lg={12}>
            <ChartCard title="Application Aging" icon={<ClockCircleOutlined />}>
              <ApplicationAgingChart
                avgTimeSinceLastActivity={
                  overviewData.aggregateByLastActivity?.average ?? 0
                }
                aging={{
                  distribution: overviewData.aggregateByLastActivity
                    ?.distribution ?? {
                    lessThan1d: 0,
                    oneTo3d: 0,
                    threeTo7d: 0,
                    sevenTo14d: 0,
                    moreThan14d: 0,
                  },
                }}
                onLegendClick={(segmentKey: string) => {
                  const titleMap: Record<string, string> = {
                    activity_lessThan1d: "Recently Active (<24 hours)",
                    activity_oneTo3d: "Active 1-3 Days Ago",
                    activity_threeTo7d: "Active 3-7 Days Ago",
                    activity_sevenTo14d: "Active 1-2 Weeks Ago",
                    activity_moreThan14d: "No Activity in 2 Weeks",
                  };
                  openDrawer(
                    segmentKey,
                    titleMap[segmentKey] || "Applications"
                  );
                }}
              />
            </ChartCard>
          </Col>

          {/* Workload Distribution */}
          <Col xs={24} lg={12}>
            <ChartCard title="Workload Distribution" icon={<UserOutlined />}>
              <WorkloadByAdminChart
                data={overviewData.aggregateByAdmin ?? []}
                onDrilldown={(
                  adminId: number,
                  firstName: string,
                  lastName: string
                ) =>
                  openDrawer(
                    `workload__${adminId}`,
                    `${
                      adminId === 0
                        ? "Unassigned Applications"
                        : `${firstName} ${lastName}'s Queue`
                    }`
                  )
                }
              />
            </ChartCard>
          </Col>

          {/* Pending Status Queues */}
          <Col xs={24} lg={12}>
            <ChartCard
              title="Pending Status Queues"
              icon={<LineChartOutlined />}
            >
              <ApplicationsByStatusChart
                data={overviewData.aggregateByDecisionStatus ?? []}
                onDrilldown={(statusId: string, title: string) =>
                  openDrawer(`status__${statusId}`, `Status: ${title}`)
                }
              />
            </ChartCard>
          </Col>

          {/* Distribution by Flow */}
          <Col xs={24} lg={12}>
            <ChartCard
              title="Distribution by Flow"
              icon={<LineChartOutlined />}
              extra={<DurationBadge duration="30d" tooltip="Last Month" />}
            >
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  height: "100%",
                }}
              >
                <div style={{ flex: 1, minHeight: "280px" }}>
                  <ApplicationsByFlowChart
                    data={overviewData.aggregateByFlow ?? []}
                    onDrilldown={(flowId: string, title: string) =>
                      openDrawer(
                        `flow_30d__${flowId}`,
                        `Flow: ${title} (Last Month)`
                      )
                    }
                  />
                </div>
                <div
                  style={{
                    marginTop: "24px",
                    paddingTop: "16px",
                    borderTop: "1px solid #f0f0f0",
                  }}
                >
                  <FlowChartLegend />
                </div>
              </div>
            </ChartCard>
          </Col>

          {/* Volume Trend */}
          <Col xs={24} lg={12}>
            <ChartCard
              title="Recent Volume"
              icon={<LineChartOutlined />}
              style={{ minHeight: "325px" }}
            >
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  height: "100%",
                }}
              >
                <div style={{ flex: 1 }}>
                  <VolumeTrendChart
                    data={overviewData.aggregateBySubmissionDate ?? []}
                    onDrilldown={(filterKey: string, title: string) => {
                      openDrawer(filterKey, title);
                    }}
                  />
                </div>
                <div
                  style={{
                    marginTop: "24px",
                    paddingTop: "16px",
                    borderTop: "1px solid #f0f0f0",
                  }}
                >
                  <VolumeTrendLegend />
                </div>
              </div>
            </ChartCard>
          </Col>

          {/* Efficiency */}
          <Col xs={24} lg={12}>
            <ChartCard
              title="Efficiency"
              icon={<ClockCircleOutlined />}
              extra={<DurationBadge duration="30d" tooltip="Last Month" />}
            >
              <EfficiencyChart
                avgTimeToDecision={
                  overviewData.aggregateByDecisionTime?.average ?? 0
                }
                avgTimeToDecisionChange={timeToDecisionChange}
                efficiency={efficiencyMetrics}
                onLegendClick={(segmentKey: string) => {
                  const titleMap: Record<string, string> = {
                    decisioned_lessThan1d_30d:
                      "Same-Day Decisions (Last Month)",
                    decisioned_oneTo2d_30d: "Next-Day Decisions (Last Month)",
                    decisioned_twoTo4d_30d: "2-4 Day Decisions (Last Month)",
                    decisioned_fourTo7d_30d: "4-7 Day Decisions (Last Month)",
                    decisioned_moreThan7d_30d: "Week+ Decisions (Last Month)",
                  };
                  openDrawer(
                    segmentKey,
                    titleMap[segmentKey] || "Applications"
                  );
                }}
              />
            </ChartCard>
          </Col>
        </Row>
      </main>

      {/* Overview Settings Modal */}
      <ApplicationsOverviewSettingsModal
        visible={settingsModalVisible}
        onClose={() => setSettingsModalVisible(false)}
        organizationKey={fiSlug || ""}
        onSave={() => fetchRealData(true, true)}
      />

      {/* Drilldown Modal */}
      <Modal
        title={
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              width: "100%",
              paddingBottom: "16px",
              borderBottom: "1px solid #d9d9d9",
            }}
          >
            <h4 className="f-5" style={{ margin: 0 }}>
              {modalTitle}
            </h4>
            <div className="d-flex align-items-center" style={{ gap: "8px" }}>
              <input
                type="text"
                className="form-control"
                placeholder="Search"
                value={modalSearch}
                onChange={(e) => setModalSearch(e.target.value)}
                style={{ width: "300px" }}
              />
              <button
                type="button"
                className="btn btn-outline-primary"
                onClick={clearAllFilters}
                style={{ whiteSpace: "nowrap" }}
              >
                Clear Filters
              </button>
              <button
                type="button"
                className="btn btn-outline-primary"
                onClick={handleExportCsv}
                title="Export to CSV"
                disabled={!exportCsvHandler}
              >
                <span className="oi oi-data-transfer-download"></span>
              </button>
              <button
                type="button"
                className="close"
                aria-label="Close"
                onClick={closeDrawer}
                style={{
                  margin: 0,
                  padding: 0,
                  fontSize: "1.5rem",
                  marginLeft: "16px",
                }}
              >
                <span aria-hidden="true">&times;</span>
              </button>
            </div>
          </div>
        }
        open={modalVisible}
        onCancel={closeDrawer}
        closeIcon={null}
        footer={null}
        width="calc(100vw - 32px)"
        style={{ top: 16, maxWidth: "none" }}
        styles={{
          body: {
            height: "calc(100vh - 154px)",
            padding: 0,
            overflow: "hidden",
          },
        }}
        destroyOnClose
      >
        <div style={{ height: "100%", overflow: "auto", paddingTop: "16px" }}>
          <FilterableDashboard
            search={modalSearch}
            activeFilter={modalFilter}
            dashboardFilter={modalOverviewFilter}
            includeArchived={false}
            setClearColumnFiltersHandler={setClearColumnFiltersHandler}
            setExportCsvHandler={setExportCsvHandler}
            fiSlug={fiSlug}
            userId={userId}
          />
        </div>
      </Modal>
    </div>
  );
};

export default ApplicationsOverview;
